import { Channel, TUtmInfo } from 'types/common';
import { TSubscriptionItem } from 'types/subscription';
import { Event } from 'types/calendar';
import { getPreviewProviderId } from './sessionStorage';

const PREFIX = 'box_practice';

const isSecureEnv = (): boolean => window.location.protocol === 'https:';

const setCookieToken = (name: string, value: string, maxAgeDays = 15) => {
    document.cookie = `${name}=${encodeURIComponent(value)}; Secure; SameSite=Lax; Path=/; Max-Age=${maxAgeDays * 86400}`;
};

const getCookieToken = (name: string): string | undefined => {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : undefined;
};

const removeCookieToken = (name: string) => {
    document.cookie = `${name}=; Secure; SameSite=Lax; Path=/; Max-Age=0`;
};

export enum EKey {
    USER_ID = 'user_id',
    PROVIDER = 'provider',
    GOOGLE_TOKEN = 'google_token',
    GOOGLE_TOKEN_EXPIRED_AT = 'google_token_expired_at',
    TIME_ZONE = 'time_zone',
    BOX_PRACTICE_TOKEN = 'box_practice_token',
    TERMS_FOR_EXPORT = 'terms_for_export',
    OPS_TOKEN = 'ops_token',
    HAS_ONCE_SUCCESS_LOGIN = 'has_once_success_login',
    PF_URL = 'pf_url',
    HAS_VIEW_PROSPECT_INFO = 'has_view_prospect_info',
    HAS_VIEW_APPOINTMENT_INFO = 'has_view_appointment_info',
    SUBSCRIPTION_CALLBACK_PATH = 'subscription_callback_path',
    SUBSCRIPTION_CALLBACK_SHOW_SUCCESS = 'subscription_callback_show_success',
    PROVIDER_HAS_SET_NOTIFY_EMAILS = 'provider_has_set_notify_emails',
    PENDING_SUBSCRIPTION_CHANNEL = 'pending_subscription_channel',
    PENDING_SUBSCRIPTION_PLAN = 'pending_subscription_plan',
    HAS_VIEW_LOCAL_SEO_INFO = 'has_view_local_seo_info',
    DISABLE_SURVEY_CAP_NOTICE = 'disable_survey_cap_notice',
    HAS_SHOW_SURVEY_TOOLTIPS = 'has_show_survey_tooltips',
    IS_EDITING_SURVEY = 'is_editing_surver',
    IS_EDITING_REVIEW = 'is_editing_review',
    REPUTATION_SETUP_STEP = 'reputation_setup_step',
    IMPROTED_NEW_PATIENTS = 'imported_new_patients',
    AVA_DOCUMENT_ID = 'ava_document_id',
    DISABLE_SHOW_DIRECT_LINK_IN_PROSPECT = 'disable_show_direct_link_in_prospect',
    ELIGIBILITY_SKIP_COST_MODAL = 'eligibility_skip_cost_modal',
    HAS_DISABLE_VIEW_SERVICE_CHECK = 'has_disable_view_service_check',
    OPS_RECENT_SEARCH = 'recent_search',
    HAS_VIEW_KIWI_EHR_PAGE = 'has_view_kiwi_ehr_page',
    HAS_VIEW_KLARITY_REVIEW_PAGE = 'has_view_klarity_review_page',
    HAS_DONE_KLARITY_TUTORIAL = 'has_done_klarity_tutorial',
    HAS_SHOW_UPLOAD_REVIEWS_MODAL = 'has_show_upload_reviews_modal',
    HAS_DISMISS_UPLOAD_REVIEWS_BANNER = 'has_dismiss_upload_reviews_banner',
    HAS_DISMISS_REVIEW_LINK_COACHMARK = 'has_dismiss_review_link_coachmark',
    HAS_SHOW_AVA_SETTING_IN_PROSEPECT = 'has_show_ava_setting_in_prosepect',
    UTM_INFO = 'utm_info',
    HIDE_CMD_FOREVER = 'hide_cmd_forever', // hide channel management delegation forever https://www.figma.com/design/rWhgWwp3dNeYzTPJMPoqVP/Channels?node-id=3350-39286&m=dev
    SHOW_KIWI_PLAN_ADVERTISE_TIMES = 'show_kiwi_plan_advertise_times',
    SHOW_PRACTICE_FRONT_THEME = 'show_practice_front_theme',
    HAS_SHOW_WEBINAR_MODAL = 'has_show_webinar_modal',
    REFERRAL_LINK_MAP = 'referral_link_map',
    HAS_AUTO_SHOW_ONBOARDING_POPUP = 'has_auto_show_onboarding_popup',
    HAS_DISMISS_INSURANCE_SURVEY_MODAL = 'has_dismiss_insurance_survey_modal',
    HAS_SHOW_HELPCENTER_POPUP_FOR_KLARITYUSER = 'has_show_helpcenter_popup_for_klarityuser',
    HAS_SHOW_CALENDAR_ONBOARDING_POPUP_250315 = 'has_show_calendar_onboarding_popup_250315',
    HAS_SHOW_CALENDAR_ONBOARDING_POPUP_250415 = 'has_show_calendar_onboarding_popup_250415',
    AUTH_GOOGLE_CALENDAR_CALLBACK_PATH = 'auth_google_calendar_callback_path',
    HIDE_CALENDAR_TIPS = 'hide_calendar_tips',
    HAS_SHOW_TOUR_TIPS = 'has_show_tour_tips',
    TOUR_TIP_EVENT = 'tour_tip_event',
    PAYMENT_SETUP_STEP = 'payment_setup_step',
    HAS_INIT_SAMPLE_CLIENT = 'has_init_sample_client',
    UNI_PROFILE_STEP = 'uni_profile_step',
    PPA_ADVANCE_SETTING_STEP = 'ppa_advance_setting_step',
    HAS_CLICK_JOIN_PPA_FROM_BANNER = 'has_click_join_ppa_from_banner',
    HAS_AUTO_POP_JOIN_PPA_MODAL = 'has_auto_pop_join_ppa_modal',
    HAS_SHOWN_REMINDER_BANNER = 'has_shown_reminder_banner',
    HAS_SHOW_AVA_SETTING_IN_INBOX = 'has_show_ava_setting_in_inbox',
    PPA_CHANNEL_ID = 'ppa_channel_id',
    HAS_SHOW_WEBINAR_MODAL_IN_DASH_BOARD = 'has_show_webinar_modal_in_dash_board',
    UNIT_ID = 'unit_id',
    HAS_SHOW_TEXT_VISIT_PAGE = 'has_show_text_visit_page',
    HAS_CLOSE_SET_MORE_TIME_BANNER = 'has_close_set_more_time_banner',
    HAS_CLOSE_OPPORTUNITY_BANNER = 'has_close_opportunity_banner',
    HAS_AUTO_POP_INVOICE_PAYBACK_TERM_MODAL = 'has_auto_pop_invoice_payback_term_modal',
    HAS_CLOSE_PAY_LATER_BANNER_IN_FINANCIALS = 'has_close_pay_later_banner_in_financials',
    HAS_CLOSE_PAY_LATER_BANNER_IN_DASHBOARD = 'has_close_pay_later_banner_in_dashboard',
    HAS_CLOSE_ASKCHART_WELCOME_MODAL = 'has_close_askchart_welcome_modal',
    HAS_CLOSE_ASKCHART_CALENDAR_BANNER = 'has_close_askchart_calendar_banner',
    HAS_CLOSE_BILLING_CALENDAR_BANNER = 'has_close_billing_calendar_banner',
    HAS_CLOSE_ZOOM_VIDEO_VISITS_BANNER_IN_DASHBOARD = 'has_close_zoom_video_visits_banner_in_dashboard',
    HAS_SKIP_GREETINGS = 'has_skip_greetings',
    HAS_CLOSE_GROWTH_TIPS = 'has_close_growth_tips',
    PROVIDER_TOKEN_SET_TIME = 'provider_token_set_time',
    HAS_SHOW_ACCEPT_DRAWER_TOUR = 'has_show_accept_drawer_tour',
    HAS_SHOW_PRESCRIPTION_CONFIG_MODAL = 'has_show_prescription_config_modal',
    HAS_SHOW_CHAT_INPUT_TOUR = 'has_show_chat_input_tour',
    HAS_CLOSE_PAY_LATER_TIPS_IN_FINANCIALS = 'has_close_pay_later_tips_in_financials',
    SCHEDULE_TOKEN = 'schedule_token', //provider
    ADMIN_TOKEN = 'admin_token', //admin
    HAS_HIDE_FAX_NUMBER_BANNER = 'has_hide_fax_number_banner',
    REVIEW_GROUP_PROVIDER_IDS = 'review_group_provider_ids',
    LOGIN_COUNT = 'login_count',
    MEETING_LINK_SETTING_SIDER_MENU = 'meeting_link_setting_sider_menu',
    MEETING_LINK_SETTING_CALENDAR_PAGE = 'meeting_link_setting_calendar_page',
    // 上次在 Credentialing info 里点 Update 确认更新的时间戳（ms）。用于 24h 内不再重复弹确认框。
    CRED_INFO_UPDATE_CONFIRMED_AT = 'cred_info_update_confirmed_at',
    HAS_SHOW_VISIT_TEMPLATE_GUIDE = 'has_show_visit_template_guide',
    SIDEBAR_COLLAPSED = 'sidebar_collapsed',
}

export enum EDot {
    PROSPECT_0810 = 'prospect_0810',
    MARKETING_0903 = 'marketing_0903',
    TEXT_VISIT_0923 = 'text_visit_0923',
    PRESCRIPTIONS_1021 = 'prescriptions_1021',
    EFAX_041126 = 'efax_041126',
    SETTINGS_1121 = 'settings_1121',
    FINANCIALS_TAG_1128 = 'financials_tag_1128',
    EMAIL_NOTIFICATIONS_NEW_TEXT_VISIT = 'email_notifications_new_text_visit',
    INSURANCE_BILLING_0817 = 'insurance_billing_0817',
}

const getUserId = (): number | undefined => {
    const previewProviderId = getPreviewProviderId();
    if (previewProviderId) {
        try {
            return parseInt(previewProviderId, 10);
        } catch (e) {}
    }

    const key = `${PREFIX}_${EKey.USER_ID}`;
    const str = localStorage.getItem(key);
    if (!str) {
        return undefined;
    }
    return parseInt(str, 10);
};

const setUserId = (id?: number) => {
    const key = `${PREFIX}_${EKey.USER_ID}`;
    if (!id) {
        localStorage.removeItem(key);
    }
    localStorage.setItem(key, `${id}`);
};

export const getKey = (key: string, withUserId?: boolean): string => {
    let _key = `${PREFIX}_${key}`;
    if (withUserId && getUserId()) {
        _key = `${_key}_${getUserId()}`;
    }
    return _key;
};

const setProvider = (data?: any) => {
    const key = getKey(EKey.PROVIDER);
    if (!data) {
        localStorage.removeItem(key);
    }
    localStorage.setItem(key, JSON.stringify(data));
};

const getProvider = (): any | undefined => {
    const key = getKey(EKey.PROVIDER);
    const str = localStorage.getItem(key);
    if (!str) {
        return undefined;
    }
    return JSON.parse(str);
};

const setUserToken = (token?: string) => {
    const key = getKey(EKey.BOX_PRACTICE_TOKEN);
    if (!token) {
        localStorage.removeItem(key);
        if (isSecureEnv()) removeCookieToken(key);
    } else if (isSecureEnv()) {
        setCookieToken(key, token);
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, `${token}`);
    }
};

const getUserToken = (): string | undefined => {
    const key = getKey(EKey.BOX_PRACTICE_TOKEN);
    if (isSecureEnv()) {
        const cookieVal = getCookieToken(key);
        if (cookieVal) return cookieVal;
    }
    return localStorage.getItem(key) || undefined;
};

const getScheduleToken = (): string => {
    const key = getKey(EKey.SCHEDULE_TOKEN);
    if (isSecureEnv()) {
        const cookieVal = getCookieToken(key);
        if (cookieVal) return cookieVal;
    }
    return localStorage.getItem(key) || '';
};

const setScheduleToken = (token?: string) => {
    const key = getKey(EKey.SCHEDULE_TOKEN);
    if (!token) {
        localStorage.removeItem(key);
        if (isSecureEnv()) removeCookieToken(key);
    } else if (isSecureEnv()) {
        setCookieToken(key, token);
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, token);
    }
};

const getAdminToken = (): string => {
    const key = getKey(EKey.ADMIN_TOKEN);
    if (isSecureEnv()) {
        const cookieVal = getCookieToken(key);
        if (cookieVal) return cookieVal;
    }
    return localStorage.getItem(key) || '';
};

const setAdminToken = (token?: string) => {
    const key = getKey(EKey.ADMIN_TOKEN);
    if (!token) {
        localStorage.removeItem(key);
        if (isSecureEnv()) removeCookieToken(key);
    } else if (isSecureEnv()) {
        setCookieToken(key, token);
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, token);
    }
};

const setGoogleToken = (token?: string) => {
    const key = getKey(EKey.GOOGLE_TOKEN);
    if (!token) {
        localStorage.removeItem(key);
    }
    localStorage.setItem(key, token!);
};

const getGoogleToken = (): string | undefined => {
    const key = getKey(EKey.GOOGLE_TOKEN);
    return localStorage.getItem(key) || undefined;
};

const setGoogleTokenExpiredAt = (time?: number) => {
    const key = getKey(EKey.GOOGLE_TOKEN_EXPIRED_AT);
    if (!time) {
        localStorage.removeItem(key);
    }
    localStorage.setItem(key, `${time}`);
};

const getGoogleTokenExpiredAt = (): number | undefined => {
    const key = getKey(EKey.GOOGLE_TOKEN_EXPIRED_AT);
    const str = localStorage.getItem(key);
    if (!str) {
        return undefined;
    }
    return parseInt(str, 10);
};

const setTimeZone = (timeZone: string) => {
    const key = getKey(EKey.TIME_ZONE);
    if (!timeZone) {
        localStorage.removeItem(key);
    }
    localStorage.setItem(key, timeZone);
};

const getTimeZone = (): string | undefined => {
    const key = getKey(EKey.TIME_ZONE);
    const str = localStorage.getItem(key);
    if (!str) {
        return undefined;
    }
    return str;
};

const setHasReadTermsForExport = (val?: string) => {
    const key = getKey(EKey.TERMS_FOR_EXPORT);
    if (!val) {
        localStorage.removeItem(key);
    }
    localStorage.setItem(key, val!);
};

const getHasReadTermsForExport = (): boolean => {
    const key = getKey(EKey.TERMS_FOR_EXPORT);
    return !!localStorage.getItem(key);
};

const setHasOnceSuccessLogin = (val: string) => {
    const key = getKey(EKey.HAS_ONCE_SUCCESS_LOGIN);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getHasOnceSuccessLogin = (): boolean => {
    const key = getKey(EKey.HAS_ONCE_SUCCESS_LOGIN);
    return !!localStorage.getItem(key);
};

const getLoginCount = (): number => Number(localStorage.getItem(getKey(EKey.LOGIN_COUNT, true))) || 0;

const increaseLoginCount = () => {
    const count = getLoginCount();
    if (count >= 10) return;
    localStorage.setItem(getKey(EKey.LOGIN_COUNT, true), `${count + 1}`);
};

const setPFUrl = (url?: string) => {
    const key = getKey(EKey.PF_URL);
    if (!url) {
        localStorage.removeItem(key);
    }
    localStorage.setItem(key, url!);
};

const getPFUrl = (): string | '' => {
    const key = getKey(EKey.PF_URL);
    return localStorage.getItem(key) || '';
};

const setHasViewProspect = (val?: string) => {
    const key = getKey(EKey.HAS_VIEW_PROSPECT_INFO);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getHasViewProspect = (): boolean => {
    const key = getKey(EKey.HAS_VIEW_PROSPECT_INFO);
    return !!localStorage.getItem(key);
};

const setHasViewAppointment = (val?: string) => {
    const key = getKey(EKey.HAS_VIEW_APPOINTMENT_INFO);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getHasViewAppointment = (): boolean => {
    const key = getKey(EKey.HAS_VIEW_APPOINTMENT_INFO);
    return !!localStorage.getItem(key);
};

const setSubscriptionCallbackShowSuccess = (val: boolean) => {
    const key = getKey(EKey.SUBSCRIPTION_CALLBACK_SHOW_SUCCESS);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, `${val}`);
    }
};

const getSubscriptionCallbackShowSuccess = (): boolean => {
    const key = getKey(EKey.SUBSCRIPTION_CALLBACK_SHOW_SUCCESS);
    return !!localStorage.getItem(key);
};

const setSubscriptionCallbackPath = (path: string) => {
    const key = getKey(EKey.SUBSCRIPTION_CALLBACK_PATH);
    if (!path) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, `${path}`);
    }
};

const getSubscriptionCallbackPath = (): string | null => {
    const key = getKey(EKey.SUBSCRIPTION_CALLBACK_PATH);
    return localStorage.getItem(key);
};

const setHasSetNotifyEmails = (val?: string) => {
    const key = getKey(EKey.PROVIDER_HAS_SET_NOTIFY_EMAILS);
    if (!val) {
        localStorage.removeItem(key);
    }
    localStorage.setItem(key, val!);
};

const getHasSetNotifyEmails = (): boolean => {
    const key = getKey(EKey.PROVIDER_HAS_SET_NOTIFY_EMAILS);
    return !!localStorage.getItem(key);
};

const setPendingSubscriptionChannel = (channel?: Channel) => {
    const key = getKey(EKey.PENDING_SUBSCRIPTION_CHANNEL);
    if (!channel) {
        localStorage.removeItem(key);
    } else {
        try {
            localStorage.setItem(key, JSON.stringify(channel));
        } catch (e) {
            console.error(e);
        }
    }
};

const getPendingSubscriptionChannel = (): Channel | undefined => {
    const key = getKey(EKey.PENDING_SUBSCRIPTION_CHANNEL);
    const str = localStorage.getItem(key);
    try {
        if (str) {
            return JSON.parse(str) as Channel;
        }
    } catch (e) {
        console.error(e);
    }
    return undefined;
};

const setPendingSubscriptionPlan = <T>(plan?: T): void => {
    const key = getKey(EKey.PENDING_SUBSCRIPTION_PLAN);
    if (!plan) {
        localStorage.removeItem(key);
    } else {
        try {
            localStorage.setItem(key, JSON.stringify(plan));
        } catch (e) {
            console.error(e);
        }
    }
};

const getPendingSubscriptionPlan = (): TSubscriptionItem | undefined => {
    const key = getKey(EKey.PENDING_SUBSCRIPTION_PLAN);
    const str = localStorage.getItem(key);
    try {
        if (str) {
            return JSON.parse(str) as TSubscriptionItem;
        }
    } catch (e) {
        console.error(e);
    }
    return undefined;
};

const setHasViewLocalSeoInfo = (val?: string) => {
    const key = getKey(EKey.HAS_VIEW_LOCAL_SEO_INFO);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getHasViewLocalSeoInfo = (): boolean => {
    const key = getKey(EKey.HAS_VIEW_LOCAL_SEO_INFO);
    return !!localStorage.getItem(key);
};

const getDisableSurveyCapNotice = (): boolean => {
    const key = getKey(EKey.DISABLE_SURVEY_CAP_NOTICE);
    return !!localStorage.getItem(key);
};

const setDisableSurveyCapNotice = (val?: string) => {
    const key = getKey(EKey.DISABLE_SURVEY_CAP_NOTICE);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getHasShowSurveyTooltips = (): boolean => {
    const key = getKey(EKey.HAS_SHOW_SURVEY_TOOLTIPS);
    return !!localStorage.getItem(key);
};

const setHasShowSurveyTooltips = (val?: string) => {
    const key = getKey(EKey.HAS_SHOW_SURVEY_TOOLTIPS);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const setStartEditSurveyForm = (val?: string) => {
    const key = getKey(EKey.IS_EDITING_SURVEY);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getIsStartEditSurveyForm = () => {
    const key = getKey(EKey.IS_EDITING_SURVEY);
    return localStorage.getItem(key) === '1';
};

const setStartEditReview = (val?: string) => {
    const key = getKey(EKey.IS_EDITING_REVIEW);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getIsStartEditReview = () => {
    const key = getKey(EKey.IS_EDITING_REVIEW);
    return localStorage.getItem(key) === '1';
};

// Credentialing info 更新确认弹窗的 24h 抑制：全局（按当前登录用户）
const CRED_INFO_UPDATE_CONFIRM_TTL = 24 * 60 * 60 * 1000;
const markCredInfoUpdateConfirmed = () => {
    const key = getKey(EKey.CRED_INFO_UPDATE_CONFIRMED_AT, true);
    localStorage.setItem(key, `${Date.now()}`);
};
const isCredInfoUpdateConfirmedRecently = () => {
    const key = getKey(EKey.CRED_INFO_UPDATE_CONFIRMED_AT, true);
    const str = localStorage.getItem(key);
    if (!str) return false;
    const ts = parseInt(str, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < CRED_INFO_UPDATE_CONFIRM_TTL;
};

const setReputationSetupStep = (val?: string) => {
    const key = getKey(EKey.REPUTATION_SETUP_STEP);

    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getReputationSetupStep = () => {
    const key = getKey(EKey.REPUTATION_SETUP_STEP);
    return localStorage.getItem(key);
};

const setImportedPatients = (val?: string) => {
    const key = getKey(EKey.IMPROTED_NEW_PATIENTS);

    if (!val) {
        sessionStorage.removeItem(key);
    } else {
        sessionStorage.setItem(key, val!);
    }
};

const getImportedPatients = () => {
    const key = getKey(EKey.IMPROTED_NEW_PATIENTS);
    return sessionStorage.getItem(key);
};

const setAvaDocumentId = (val?: string) => {
    const key = getKey(EKey.AVA_DOCUMENT_ID);

    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getAvaDocumentId = () => {
    const key = getKey(EKey.AVA_DOCUMENT_ID);
    return localStorage.getItem(key);
};

const getDisableShowDirectLinkInProspect = (): boolean => {
    const key = getKey(EKey.DISABLE_SHOW_DIRECT_LINK_IN_PROSPECT);
    return !!localStorage.getItem(key);
};

const setDisableShowDirectLinkInProspect = (val?: string) => {
    const key = getKey(EKey.DISABLE_SHOW_DIRECT_LINK_IN_PROSPECT);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

// eligibility check 计费确认弹窗的 "Don't ask again"（provider 级）
const getEligibilitySkipCostModal = (): boolean => {
    const key = getKey(EKey.ELIGIBILITY_SKIP_COST_MODAL, true);
    return !!localStorage.getItem(key);
};

const setEligibilitySkipCostModal = () => {
    const key = getKey(EKey.ELIGIBILITY_SKIP_COST_MODAL, true);
    localStorage.setItem(key, 'true');
};

const getHasDisableViewServiceCheck = (): boolean => {
    const key = getKey(EKey.HAS_DISABLE_VIEW_SERVICE_CHECK);
    return !!localStorage.getItem(key);
};

const setHasDisableViewServiceCheck = (val?: string) => {
    const key = getKey(EKey.HAS_DISABLE_VIEW_SERVICE_CHECK);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getOpsRecentSearch = (): string[] => {
    const key = getKey(EKey.OPS_RECENT_SEARCH);
    const val = localStorage.getItem(key);
    if (val) {
        try {
            return JSON.parse(val) as string[];
        } catch (e) {
            console.error(e);
        }
    }
    return [];
};

const addOpsRecentSearch = (add: string) => {
    const key = getKey(EKey.OPS_RECENT_SEARCH);
    const val = localStorage.getItem(key);
    if (val) {
        try {
            let valArr: string[] = JSON.parse(val) as string[];
            if (!valArr.includes(add)) {
                valArr.unshift(add);
            }
            valArr = valArr.slice(0, 3);
            localStorage.setItem(key, JSON.stringify(valArr));
            return valArr;
        } catch (e) {
            console.error(e);
        }
    } else {
        localStorage.setItem(key, JSON.stringify([add]));
    }
    return [];
};

const setHasViewKiwiEhrPage = (val?: 'true') => {
    const key = getKey(EKey.HAS_VIEW_KIWI_EHR_PAGE);
    if (!val) {
        localStorage.removeItem(key);
    }
    localStorage.setItem(key, val!);
};

const getHasViewKiwiEhrPage = (): boolean => {
    const key = getKey(EKey.HAS_VIEW_KIWI_EHR_PAGE);
    return !!localStorage.getItem(key);
};

const setHasViewKlarityReviewPage = (val?: 'true') => {
    const key = getKey(EKey.HAS_VIEW_KLARITY_REVIEW_PAGE);
    if (!val) {
        localStorage.removeItem(key);
    }
    localStorage.setItem(key, val!);
};

const getHasViewKlarityReviewPage = (): boolean => {
    const key = getKey(EKey.HAS_VIEW_KLARITY_REVIEW_PAGE);
    return !!localStorage.getItem(key);
};

const setHasDoneKlarityTutorial = (val?: 'true') => {
    const key = `${getKey(EKey.HAS_DONE_KLARITY_TUTORIAL)}_${getUserId()}`;
    if (!val) {
        localStorage.removeItem(key);
    }
    localStorage.setItem(key, val!);
};

const getHasDoneKlarityTutorial = (): boolean => {
    const key = `${getKey(EKey.HAS_DONE_KLARITY_TUTORIAL)}_${getUserId()}`;
    return !!localStorage.getItem(key);
};

const setHideChannelManagementDelegationForever = (val?: string) => {
    const key = getKey(EKey.HIDE_CMD_FOREVER);
    if (!val) {
        localStorage.removeItem(key);
    }
    localStorage.setItem(key, val!);
};

const getHideChannelManagementDelegationForever = (): boolean => {
    const key = getKey(EKey.HIDE_CMD_FOREVER);

    return !!localStorage.getItem(key);
};

const setShowAvaSettingInProsepect = (val?: 'true') => {
    const key = `${getKey(EKey.HAS_SHOW_AVA_SETTING_IN_PROSEPECT)}_${getUserId()}`;
    if (!val) {
        localStorage.removeItem(key);
    }
    localStorage.setItem(key, val!);
};

const getShowAvaSettingInProsepect = (): boolean => {
    const key = `${getKey(EKey.HAS_SHOW_AVA_SETTING_IN_PROSEPECT)}_${getUserId()}`;
    return !!localStorage.getItem(key);
};

const setHasViewDotPage = (target: EDot, val?: string) => {
    const key = `${getKey(`DOT_${target}`)}_${getUserId()}`;
    if (!val) {
        localStorage.removeItem(key);
    }
    localStorage.setItem(key, val!);
};

const getHasViewDotPage = (target: EDot): boolean => {
    const key = `${getKey(`DOT_${target}`)}_${getUserId()}`;
    return !!localStorage.getItem(key);
};

const getUtmInfo = (): TUtmInfo => {
    const key = getKey(EKey.UTM_INFO);
    const val = localStorage.getItem(key);
    if (val) {
        try {
            return JSON.parse(val);
        } catch (e) {
            return {};
        }
    }
    return {};
};

const setUtmInfo = (val?: TUtmInfo) => {
    const key = getKey(EKey.UTM_INFO);
    const current = getUtmInfo();
    const newVal = { ...current, ...(val || {}) };
    localStorage.setItem(key, JSON.stringify(newVal));
};

//clear after sign up
const clearUtmInfo = () => {
    const key = getKey(EKey.UTM_INFO);
    localStorage.removeItem(key);
};

const setShowKiwiPlanAdvertiseTimes = (val?: '1' | '2') => {
    const key = `${getKey(EKey.SHOW_KIWI_PLAN_ADVERTISE_TIMES)}_${getUserId()}`;
    getKey(EKey.SHOW_KIWI_PLAN_ADVERTISE_TIMES);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val);
    }
};

const getShowKiwiPlanAdvertiseTimes = (): '1' | '2' | undefined => {
    const key = `${getKey(EKey.SHOW_KIWI_PLAN_ADVERTISE_TIMES)}_${getUserId()}`;
    getKey(EKey.SHOW_KIWI_PLAN_ADVERTISE_TIMES);
    const val = localStorage.getItem(key);
    if (!val) {
        return undefined;
    }
    return localStorage.getItem(key) as '1' | '2';
};

const setHasOpenPracticeFrontThemePopup = (val: string) => {
    const key = getKey(EKey.SHOW_PRACTICE_FRONT_THEME);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getHasOpenPracticeFrontThemePopup = (): boolean => {
    const key = getKey(EKey.SHOW_PRACTICE_FRONT_THEME);
    return !!localStorage.getItem(key);
};

const setHasShowWebinarModal = (val: string) => {
    const key = getKey(EKey.HAS_SHOW_WEBINAR_MODAL);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getHasShowWebinarModal = (): boolean => {
    const key = getKey(EKey.HAS_SHOW_WEBINAR_MODAL);
    return !!localStorage.getItem(key);
};

const setReferralLinkMap = (val?: Record<string, string>) => {
    const key = getKey(EKey.REFERRAL_LINK_MAP);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, JSON.stringify(val));
    }
};

const getReferralLinkMap = (): string | null => {
    const key = getKey(EKey.REFERRAL_LINK_MAP);
    return localStorage.getItem(key);
};

const getHasShowAutoOnboardingPopup = (): boolean => {
    const key = getKey(EKey.HAS_AUTO_SHOW_ONBOARDING_POPUP);
    return !!localStorage.getItem(key);
};

const setHasShowAutoOnboardingPopup = (val?: string) => {
    const key = getKey(EKey.HAS_AUTO_SHOW_ONBOARDING_POPUP);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getHasDismissInsuranceSurveyModal = (): boolean => {
    const key = getKey(EKey.HAS_DISMISS_INSURANCE_SURVEY_MODAL);
    return !!localStorage.getItem(key);
};

const setHasDismissInsuranceSurveyModal = (val?: string) => {
    const key = getKey(EKey.HAS_DISMISS_INSURANCE_SURVEY_MODAL);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val);
    }
};

const getHasShowHelpCenterPopupForKlarityUser = (): boolean => {
    const key = getKey(EKey.HAS_SHOW_HELPCENTER_POPUP_FOR_KLARITYUSER);
    return !!localStorage.getItem(key);
};

const setHasShowHelpCenterPopupForKlarityUser = (val?: string) => {
    const key = getKey(EKey.HAS_SHOW_HELPCENTER_POPUP_FOR_KLARITYUSER);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getAutoGoogleCalendarCallbackPath = (): string | undefined => {
    const key = getKey(EKey.AUTH_GOOGLE_CALENDAR_CALLBACK_PATH);
    return localStorage.getItem(key) || undefined;
};

const setAutoGoogleCalendarCallbackPath = (path?: string) => {
    const key = getKey(EKey.AUTH_GOOGLE_CALENDAR_CALLBACK_PATH);
    if (!path) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, path!);
    }
};

const setHideCalendarTips = (val?: string) => {
    const key = getKey(EKey.HIDE_CALENDAR_TIPS);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getHideCalendarTips = (): boolean => {
    const key = getKey(EKey.HIDE_CALENDAR_TIPS);
    return !!localStorage.getItem(key);
};

const getTourTipsEvent = (): Event[] | undefined => {
    const val = localStorage.getItem(getKey(EKey.TOUR_TIP_EVENT));
    if (val) {
        try {
            return JSON.parse(val) as Event[];
        } catch (ex) {
            return undefined;
        }
    }
    return undefined;
};

const setTourTipsEvent = (val?: Event[]) => {
    const valstr = JSON.stringify(val);
    localStorage.setItem(getKey(EKey.TOUR_TIP_EVENT), valstr);
};

const setHasShowTourTips = (val?: boolean) => {
    const key = getKey(EKey.HAS_SHOW_TOUR_TIPS, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasShowTourTips = (): boolean => {
    const key = getKey(EKey.HAS_SHOW_TOUR_TIPS, true);
    return !!localStorage.getItem(key);
};

const setHasShowAcceptDrawerTour = (val?: boolean) => {
    const key = getKey(EKey.HAS_SHOW_ACCEPT_DRAWER_TOUR, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasShowAcceptDrawerTour = (): boolean => {
    const key = getKey(EKey.HAS_SHOW_ACCEPT_DRAWER_TOUR, true);
    return !!localStorage.getItem(key);
};

const setHasShowVisitTemplateGuide = (val?: boolean) => {
    const key = getKey(EKey.HAS_SHOW_VISIT_TEMPLATE_GUIDE, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasShowVisitTemplateGuide = (): boolean => {
    const key = getKey(EKey.HAS_SHOW_VISIT_TEMPLATE_GUIDE, true);
    return !!localStorage.getItem(key);
};

const getHasShowChatInputTour = (): boolean => {
    const key = getKey(EKey.HAS_SHOW_CHAT_INPUT_TOUR, true);
    return !!localStorage.getItem(key);
};

const setHasShowChatInputTour = (val?: boolean) => {
    const key = getKey(EKey.HAS_SHOW_CHAT_INPUT_TOUR, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};
const getHasShowCalendarOnboardingPopup240315 = () => {
    const key = getKey(EKey.HAS_SHOW_CALENDAR_ONBOARDING_POPUP_250315, true);
    return !!localStorage.getItem(key);
};

const setHasShowCalendarOnboardingPopup240315 = (val?: boolean) => {
    const key = getKey(EKey.HAS_SHOW_CALENDAR_ONBOARDING_POPUP_250315, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasShowCalendarOnboardingPopup240415 = () => {
    const key = getKey(EKey.HAS_SHOW_CALENDAR_ONBOARDING_POPUP_250415, true);
    return !!localStorage.getItem(key);
};

const setHasShowCalendarOnboardingPopup240415 = (val?: boolean) => {
    const key = getKey(EKey.HAS_SHOW_CALENDAR_ONBOARDING_POPUP_250415, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasInitSampleClient = () => {
    const key = getKey(EKey.HAS_INIT_SAMPLE_CLIENT, true);
    return !!localStorage.getItem(key);
};

const setHasInitSampleClient = (val?: boolean) => {
    const key = getKey(EKey.HAS_INIT_SAMPLE_CLIENT, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasClickJoinPPAFromBanner = () => {
    const key = getKey(EKey.HAS_CLICK_JOIN_PPA_FROM_BANNER, true);
    return !!localStorage.getItem(key);
};

const setHasClickJoinPPAFromBanner = (val?: boolean) => {
    const key = getKey(EKey.HAS_CLICK_JOIN_PPA_FROM_BANNER, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasAutoPopJoinPPAModal = () => {
    const key = getKey(EKey.HAS_AUTO_POP_JOIN_PPA_MODAL, true);
    return !!localStorage.getItem(key);
};

const setHasAutoPopJoinPPAModal = (val?: boolean) => {
    const key = getKey(EKey.HAS_AUTO_POP_JOIN_PPA_MODAL, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasShownReminderBanner = () => {
    const key = getKey(EKey.HAS_SHOWN_REMINDER_BANNER, true);
    return !!localStorage.getItem(key);
};

const setHasShownReminderBanner = (val?: boolean) => {
    const key = getKey(EKey.HAS_SHOWN_REMINDER_BANNER, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasShowAvaSettingInbox = () => {
    const key = getKey(EKey.HAS_SHOW_AVA_SETTING_IN_INBOX, true);
    return !!localStorage.getItem(key);
};

const setHasShowAvaSettingInbox = (val?: boolean) => {
    const key = getKey(EKey.HAS_SHOW_AVA_SETTING_IN_INBOX, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getPPAIdFromLocal = (): number | undefined => {
    const key = getKey(EKey.PPA_CHANNEL_ID);
    const str = localStorage.getItem(key);
    if (!str) {
        return undefined;
    }
    try {
        return parseInt(str, 10);
    } catch (e) {
        return undefined;
    }
    return undefined;
};

const setPPAIdFromLocal = (id?: number) => {
    const key = getKey(EKey.PPA_CHANNEL_ID);
    if (id) {
        localStorage.setItem(key, `${id}`);
    }
};

const getHasShowWebinarModalInDashBoard = (): boolean => {
    const key = getKey(EKey.HAS_SHOW_WEBINAR_MODAL_IN_DASH_BOARD, true);
    return !!localStorage.getItem(key);
};

const setHasShowWebinarModalInDashBoard = (val?: boolean) => {
    const key = getKey(EKey.HAS_SHOW_WEBINAR_MODAL_IN_DASH_BOARD, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getUnitId = (): string => {
    if (typeof window === 'undefined') {
        return Math.random().toString(36).substring(2);
    }
    const key = getKey(EKey.UNIT_ID);
    const str = localStorage.getItem(key);
    if (str) {
        return str;
    }
    const uid = Math.random().toString(36).substring(2);
    localStorage.setItem(key, uid);
    return uid;
};

const setUnitId = (uid?: string): void => {
    if (typeof window === 'undefined') {
        return;
    }
    if (!uid) {
        localStorage.removeItem(getKey(EKey.UNIT_ID));
        return;
    }
    localStorage.setItem(getKey(EKey.UNIT_ID), uid);
};

const getHasShowTextVisitPage = () => {
    const key = getKey(EKey.HAS_SHOW_TEXT_VISIT_PAGE, true);
    return !!localStorage.getItem(key);
};

const setHasShowTextVisitPage = (val?: boolean) => {
    const key = getKey(EKey.HAS_SHOW_TEXT_VISIT_PAGE, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasCloseSetMoreTimeBanner = () => {
    const key = getKey(EKey.HAS_CLOSE_SET_MORE_TIME_BANNER, true);
    return !!localStorage.getItem(key);
};

const setHasCloseSetMoreTimeBanner = (val?: boolean) => {
    const key = getKey(EKey.HAS_CLOSE_SET_MORE_TIME_BANNER, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasCloseOpportunityBanner = () => {
    const key = getKey(EKey.HAS_CLOSE_OPPORTUNITY_BANNER, true);
    return !!localStorage.getItem(key);
};

const setHasCloseOpportunityBanner = (val?: boolean) => {
    const key = getKey(EKey.HAS_CLOSE_OPPORTUNITY_BANNER, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasShowUploadReviewsModal = (): boolean => {
    const key = `${getKey(EKey.HAS_SHOW_UPLOAD_REVIEWS_MODAL)}_${getUserId()}`;
    return !!localStorage.getItem(key);
};

const setHasShowUploadReviewsModal = (val?: 'true') => {
    const key = `${getKey(EKey.HAS_SHOW_UPLOAD_REVIEWS_MODAL)}_${getUserId()}`;
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getHasDismissReviewLinkCoachmark = (): boolean => {
    const key = `${getKey(EKey.HAS_DISMISS_REVIEW_LINK_COACHMARK)}_${getUserId()}`;
    return !!localStorage.getItem(key);
};

const setHasDismissReviewLinkCoachmark = (val?: 'true') => {
    const key = `${getKey(EKey.HAS_DISMISS_REVIEW_LINK_COACHMARK)}_${getUserId()}`;
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getHasDismissUploadReviewsBanner = (): boolean => {
    const key = `${getKey(EKey.HAS_DISMISS_UPLOAD_REVIEWS_BANNER)}_${getUserId()}`;
    return !!localStorage.getItem(key);
};

const setHasDismissUploadReviewsBanner = (val?: 'true') => {
    const key = `${getKey(EKey.HAS_DISMISS_UPLOAD_REVIEWS_BANNER)}_${getUserId()}`;
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, val!);
    }
};

const getHasAutoPopInvoicePaybackTermModal = () => {
    const key = getKey(EKey.HAS_AUTO_POP_INVOICE_PAYBACK_TERM_MODAL, true);
    return !!localStorage.getItem(key);
};

const setHasAutoPopInvoicePaybackTermModal = (val?: boolean) => {
    const key = getKey(EKey.HAS_AUTO_POP_INVOICE_PAYBACK_TERM_MODAL, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasClosePayLaterBannerInFinancials = () => {
    const key = getKey(EKey.HAS_CLOSE_PAY_LATER_BANNER_IN_FINANCIALS, true);
    return !!localStorage.getItem(key);
};

const setHasClosePayLaterBannerInFinancials = (val?: boolean) => {
    const key = getKey(EKey.HAS_CLOSE_PAY_LATER_BANNER_IN_FINANCIALS, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasClosePayLaterBannerInDashboard = () => {
    const key = getKey(EKey.HAS_CLOSE_PAY_LATER_BANNER_IN_DASHBOARD, true);
    return !!localStorage.getItem(key);
};

const setHasClosePayLaterBannerInDashboard = (val?: boolean) => {
    const key = getKey(EKey.HAS_CLOSE_PAY_LATER_BANNER_IN_DASHBOARD, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasCloseAskChartWelcomeModal = () => {
    //remove pop
    return true;
    // const key = getKey(EKey.HAS_CLOSE_ASKCHART_WELCOME_MODAL, true);
    // return !!localStorage.getItem(key);
};

const setHasCloseAskChartWelcomeModal = (val?: boolean) => {
    const key = getKey(EKey.HAS_CLOSE_ASKCHART_WELCOME_MODAL, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasCloseAskChartCalendarBanner = () => {
    return true;
    const key = getKey(EKey.HAS_CLOSE_ASKCHART_CALENDAR_BANNER, true);
    return !!localStorage.getItem(key);
};

const setHasCloseAskChartCalendarBanner = (val?: boolean) => {
    const key = getKey(EKey.HAS_CLOSE_ASKCHART_CALENDAR_BANNER, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasCloseBillingCalendarBanner = () => {
    const key = getKey(EKey.HAS_CLOSE_BILLING_CALENDAR_BANNER, true);
    return !!localStorage.getItem(key);
};

const setHasCloseBillingCalendarBanner = (val?: boolean) => {
    const key = getKey(EKey.HAS_CLOSE_BILLING_CALENDAR_BANNER, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasSkipGreetings = () => {
    const key = getKey(EKey.HAS_SKIP_GREETINGS, true);
    return !!localStorage.getItem(key);
};

const setHasSkipGreetings = (val?: boolean) => {
    const key = getKey(EKey.HAS_SKIP_GREETINGS, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasCloseGrowthTips = () => {
    const key = getKey(EKey.HAS_CLOSE_GROWTH_TIPS, true);
    return !!localStorage.getItem(key);
};

const setHasCloseGrowthTips = (val?: boolean) => {
    const key = getKey(EKey.HAS_CLOSE_GROWTH_TIPS, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasCloseZoomVideoVisitsBannerInDashboard = () => {
    const key = getKey(EKey.HAS_CLOSE_ZOOM_VIDEO_VISITS_BANNER_IN_DASHBOARD, true);
    return !!localStorage.getItem(key);
};

const setHasCloseZoomVideoVisitsBannerInDashboard = (val?: boolean) => {
    const key = getKey(EKey.HAS_CLOSE_ZOOM_VIDEO_VISITS_BANNER_IN_DASHBOARD, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};
const setLocalStoreProviderTokenSetTime = (data?: number) => {
    if (typeof window === 'undefined') {
        return;
    }
    const key = getKey(EKey.PROVIDER_TOKEN_SET_TIME);
    if (!data) {
        localStorage.removeItem(key);
        return;
    }
    localStorage.setItem(key, `${data}`);
};

const getLocalStoreProviderTokenSetTime = (): number => {
    if (typeof window === 'undefined') {
        return 0;
    }
    const key = getKey(EKey.PROVIDER_TOKEN_SET_TIME);
    const str = localStorage.getItem(key);
    if (!str) {
        return 0;
    }
    return parseInt(str, 10);
};

const getHasShowPrescriptionConfigModal = (): boolean => {
    const key = getKey(EKey.HAS_SHOW_PRESCRIPTION_CONFIG_MODAL, true);
    return !!localStorage.getItem(key);
};

const setHasShowPrescriptionConfigModal = (val?: boolean) => {
    const key = getKey(EKey.HAS_SHOW_PRESCRIPTION_CONFIG_MODAL, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getHasClosePayLaterTipsInFinancials = () => {
    const key = getKey(EKey.HAS_CLOSE_PAY_LATER_TIPS_IN_FINANCIALS, true);
    return !!localStorage.getItem(key);
};

const setHasClosePayLaterTipsInFinancials = (val?: boolean) => {
    const key = getKey(EKey.HAS_CLOSE_PAY_LATER_TIPS_IN_FINANCIALS, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};
const getHasHideFaxNumberBanner = () => {
    const key = getKey(EKey.HAS_HIDE_FAX_NUMBER_BANNER, true);
    return !!localStorage.getItem(key);
};

const setHasHideFaxNumberBanner = (val?: boolean) => {
    const key = getKey(EKey.HAS_HIDE_FAX_NUMBER_BANNER, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getReviewGroupProviderIds = (): number[] => {
    const key = getKey(EKey.REVIEW_GROUP_PROVIDER_IDS);
    const str = localStorage.getItem(key);
    if (!str) return [];
    try {
        return JSON.parse(str);
    } catch {
        return [];
    }
};

const setReviewGroupProviderIds = (ids: number[]) => {
    const key = getKey(EKey.REVIEW_GROUP_PROVIDER_IDS);
    localStorage.setItem(key, JSON.stringify(ids));
};

const clearReviewGroupProviderIds = () => {
    const key = getKey(EKey.REVIEW_GROUP_PROVIDER_IDS);
    localStorage.removeItem(key);
};

const getClickedMeetingLinkSettingSiderMenu = () => {
    const key = getKey(EKey.MEETING_LINK_SETTING_SIDER_MENU, true);
    return !!localStorage.getItem(key);
};

const setClickedMeetingLinkSettingSiderMenu = (val?: boolean) => {
    const key = getKey(EKey.MEETING_LINK_SETTING_SIDER_MENU, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

const getClickedMeetingLinkSettingCalendarPage = () => {
    const key = getKey(EKey.MEETING_LINK_SETTING_CALENDAR_PAGE, true);
    return !!localStorage.getItem(key);
};

const setClickedMeetingLinkSettingCalendarPage = (val?: boolean) => {
    const key = getKey(EKey.MEETING_LINK_SETTING_CALENDAR_PAGE, true);
    if (!val) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, 'true');
    }
};

export {
    setProvider,
    getProvider,
    setUserId,
    getUserId,
    setUserToken,
    getUserToken,
    setGoogleToken,
    getGoogleToken,
    setGoogleTokenExpiredAt,
    getGoogleTokenExpiredAt,
    setTimeZone,
    getTimeZone,
    setHasReadTermsForExport,
    getHasReadTermsForExport,
    setHasOnceSuccessLogin,
    getHasOnceSuccessLogin,
    getLoginCount,
    increaseLoginCount,
    setPFUrl,
    getPFUrl,
    setHasViewProspect,
    getHasViewProspect,
    setHasViewAppointment,
    getHasViewAppointment,
    setSubscriptionCallbackShowSuccess,
    getSubscriptionCallbackShowSuccess,
    setSubscriptionCallbackPath,
    getSubscriptionCallbackPath,
    setHasSetNotifyEmails,
    getHasSetNotifyEmails,
    setPendingSubscriptionChannel,
    getPendingSubscriptionChannel,
    setPendingSubscriptionPlan,
    getPendingSubscriptionPlan,
    setHasViewLocalSeoInfo,
    getHasViewLocalSeoInfo,
    getDisableSurveyCapNotice,
    setDisableSurveyCapNotice,
    getHasShowSurveyTooltips,
    setHasShowSurveyTooltips,
    setStartEditSurveyForm,
    getIsStartEditSurveyForm,
    setStartEditReview,
    getIsStartEditReview,
    markCredInfoUpdateConfirmed,
    isCredInfoUpdateConfirmedRecently,
    setReputationSetupStep,
    getReputationSetupStep,
    setImportedPatients,
    getImportedPatients,
    setAvaDocumentId,
    getAvaDocumentId,
    getDisableShowDirectLinkInProspect,
    setDisableShowDirectLinkInProspect,
    getEligibilitySkipCostModal,
    setEligibilitySkipCostModal,
    getHasDisableViewServiceCheck,
    setHasDisableViewServiceCheck,
    addOpsRecentSearch,
    getOpsRecentSearch,
    setHasViewKiwiEhrPage,
    getHasViewKiwiEhrPage,
    setHasViewKlarityReviewPage,
    getHasViewKlarityReviewPage,
    setHasDoneKlarityTutorial,
    getHasDoneKlarityTutorial,
    setHasViewDotPage,
    getHasViewDotPage,
    setShowAvaSettingInProsepect,
    getShowAvaSettingInProsepect,
    getUtmInfo,
    setUtmInfo,
    clearUtmInfo,
    setHideChannelManagementDelegationForever,
    getHideChannelManagementDelegationForever,
    setShowKiwiPlanAdvertiseTimes,
    getShowKiwiPlanAdvertiseTimes,
    setHasOpenPracticeFrontThemePopup,
    getHasOpenPracticeFrontThemePopup,
    setHasShowWebinarModal,
    getHasShowWebinarModal,
    setReferralLinkMap,
    getReferralLinkMap,
    getHasShowAutoOnboardingPopup,
    setHasShowAutoOnboardingPopup,
    getHasDismissInsuranceSurveyModal,
    setHasDismissInsuranceSurveyModal,
    getHasShowHelpCenterPopupForKlarityUser,
    setHasShowHelpCenterPopupForKlarityUser,
    getAutoGoogleCalendarCallbackPath,
    setAutoGoogleCalendarCallbackPath,
    setHideCalendarTips,
    getHideCalendarTips,
    getTourTipsEvent,
    setTourTipsEvent,
    setHasShowTourTips,
    getHasShowTourTips,
    getHasShowCalendarOnboardingPopup240315,
    setHasShowCalendarOnboardingPopup240315,
    getHasShowCalendarOnboardingPopup240415,
    setHasShowCalendarOnboardingPopup240415,
    getHasInitSampleClient,
    setHasInitSampleClient,
    getHasClickJoinPPAFromBanner,
    setHasClickJoinPPAFromBanner,
    getHasAutoPopJoinPPAModal,
    setHasAutoPopJoinPPAModal,
    getHasShownReminderBanner,
    setHasShownReminderBanner,
    getHasShowAvaSettingInbox,
    setHasShowAvaSettingInbox,
    getPPAIdFromLocal,
    setPPAIdFromLocal,
    getHasShowWebinarModalInDashBoard,
    setHasShowWebinarModalInDashBoard,
    getUnitId,
    setUnitId,
    getHasShowTextVisitPage,
    setHasShowTextVisitPage,
    getHasCloseSetMoreTimeBanner,
    setHasCloseSetMoreTimeBanner,
    getHasCloseOpportunityBanner,
    setHasCloseOpportunityBanner,
    getHasAutoPopInvoicePaybackTermModal,
    setHasAutoPopInvoicePaybackTermModal,
    getHasClosePayLaterBannerInFinancials,
    setHasClosePayLaterBannerInFinancials,
    getHasClosePayLaterBannerInDashboard,
    setHasClosePayLaterBannerInDashboard,
    getHasCloseAskChartWelcomeModal,
    setHasCloseAskChartWelcomeModal,
    getHasCloseAskChartCalendarBanner,
    setHasCloseAskChartCalendarBanner,
    getHasCloseBillingCalendarBanner,
    setHasCloseBillingCalendarBanner,
    getHasSkipGreetings,
    setHasSkipGreetings,
    getHasCloseGrowthTips,
    setHasCloseGrowthTips,
    getHasShowUploadReviewsModal,
    setHasShowUploadReviewsModal,
    getHasDismissUploadReviewsBanner,
    setHasDismissUploadReviewsBanner,
    getHasDismissReviewLinkCoachmark,
    setHasDismissReviewLinkCoachmark,
    getHasShowAcceptDrawerTour,
    setHasShowAcceptDrawerTour,
    getHasShowVisitTemplateGuide,
    setHasShowVisitTemplateGuide,
    getHasCloseZoomVideoVisitsBannerInDashboard,
    setHasCloseZoomVideoVisitsBannerInDashboard,
    setLocalStoreProviderTokenSetTime,
    getLocalStoreProviderTokenSetTime,
    getHasShowPrescriptionConfigModal,
    setHasShowPrescriptionConfigModal,
    getHasShowChatInputTour,
    setHasShowChatInputTour,
    getHasClosePayLaterTipsInFinancials,
    setHasClosePayLaterTipsInFinancials,
    getScheduleToken,
    setScheduleToken,
    getAdminToken,
    setAdminToken,
    getHasHideFaxNumberBanner,
    setHasHideFaxNumberBanner,
    getReviewGroupProviderIds,
    setReviewGroupProviderIds,
    clearReviewGroupProviderIds,
    getClickedMeetingLinkSettingSiderMenu,
    setClickedMeetingLinkSettingSiderMenu,
    getClickedMeetingLinkSettingCalendarPage,
    setClickedMeetingLinkSettingCalendarPage,
};
